import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, ShieldCheck, UploadCloud, FileText } from 'lucide-react';

// Public, no-login page — does NOT use the AuthContext or send auth cookies.
const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public/securecx` });

const RESEND_COOLDOWN = 60;

const PublicSecureCX = ({ token }) => {
  const [link, setLink] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [step, setStep] = useState('email'); // email | code | upload
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [docs, setDocs] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null); // requested_doc label currently targeted, or null for generic
  const fileInputRef = useRef(null);
  const itemFileInputRef = useRef(null);

  useEffect(() => {
    publicApi.get(`/${token}`).then((r) => setLink(r.data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || 'This link is invalid.'));
  }, [token]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const loadDocs = (tok) => {
    publicApi.get(`/${token}/documents`, { params: { session_token: tok } }).then((r) => setDocs(r.data)).catch(() => {});
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await publicApi.post(`/${token}/request-code`, { email });
      setStep('code');
      setCooldown(RESEND_COOLDOWN);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    setBusy(true); setErr('');
    try {
      await publicApi.post(`/${token}/request-code`, { email });
      setCooldown(RESEND_COOLDOWN);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Something went wrong.'); }
    finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { data } = await publicApi.post(`/${token}/verify-code`, { email, code });
      setSessionToken(data.session_token);
      setStep('upload');
      loadDocs(data.session_token);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Incorrect code.'); }
    finally { setBusy(false); }
  };

  const uploadFiles = async (fileList, requestedDoc) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true); setErr('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('session_token', sessionToken);
        fd.append('file', file);
        if (requestedDoc) fd.append('requested_doc', requestedDoc);
        await publicApi.post(`/${token}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      loadDocs(sessionToken);
    } catch (ex) { setErr(ex?.response?.data?.detail || 'Upload failed. Please try again.'); }
    finally { setBusy(false); setUploadTarget(null); }
  };

  if (loadErr) {
    return (
      <div className="scx-pub-shell">
        <div className="scx-pub-card" data-testid="secure-link-error">
          <div className="scx-pub-title">Link unavailable</div>
          <div className="scx-pub-desc">{loadErr}</div>
        </div>
      </div>
    );
  }
  if (!link) return <div className="scx-pub-shell"><div className="scx-pub-card">Loading…</div></div>;

  return (
    <div className="scx-pub-shell">
      <div className="scx-pub-card" data-testid="secure-page">
        <div className="scx-pub-badge"><ShieldCheck size={13} />Secure Document Upload</div>
        <div className="scx-pub-title">{link.label}</div>

        {step !== 'upload' && link.requested_documents?.length > 0 && (
          <div className="scx-pub-checklist" data-testid="secure-requested-documents">
            <div className="scx-pub-checklist-hd">Documents requested</div>
            {link.requested_documents.map((d, i) => (
              <div key={i} className="scx-pub-checklist-item"><FileText size={12} />{d}</div>
            ))}
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={sendCode} data-testid="secure-email-form">
            <div className="scx-pub-desc">Enter your email to verify your identity before uploading.</div>
            <div className="scx-pub-field">
              <label>Email address</label>
              <input type="email" className="scx-pub-input" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="secure-email-input" />
            </div>
            {err && <div className="scx-pub-error" data-testid="secure-email-error">{err}</div>}
            <button type="submit" className="scx-pub-btn" disabled={busy} data-testid="secure-email-submit">{busy ? 'Sending…' : 'Send verification code'}</button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={verifyCode} data-testid="secure-code-form">
            <div className="scx-pub-desc">We sent a 6-digit code to <b>{email}</b>. Enter it below to continue.</div>
            <div className="scx-pub-field">
              <label>Verification code</label>
              <input
                type="text" inputMode="numeric" maxLength={6} className="scx-pub-input scx-pub-code-input"
                required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" data-testid="secure-code-input"
              />
            </div>
            {err && <div className="scx-pub-error" data-testid="secure-code-error">{err}</div>}
            <button type="submit" className="scx-pub-btn" disabled={busy || code.length !== 6} data-testid="secure-code-submit">{busy ? 'Verifying…' : 'Verify & continue'}</button>
            <button type="button" className="scx-pub-btn-secondary" disabled={cooldown > 0 || busy} onClick={resendCode} data-testid="secure-resend-code">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        {step === 'upload' && (
          <div data-testid="secure-upload-step">
            <div className="scx-pub-desc"><CheckCircle2 size={13} className="scx-pub-success-ic" style={{ marginRight: 4, verticalAlign: -2 }} />Verified as <b>{email}</b>. Upload your documents below.</div>

            <input ref={itemFileInputRef} type="file" style={{ display: 'none' }} onChange={(e) => uploadFiles(e.target.files, uploadTarget)} data-testid="secure-item-file-input" />

            {link.requested_documents?.length > 0 && (
              <div className="scx-pub-checklist" data-testid="secure-requested-documents">
                <div className="scx-pub-checklist-hd">
                  Documents requested ({docs.filter((d) => link.requested_documents.includes(d.requested_doc)).reduce((set, d) => set.add(d.requested_doc), new Set()).size}/{link.requested_documents.length} received)
                </div>
                {link.requested_documents.map((reqLabel, i) => {
                  const matches = docs.filter((d) => d.requested_doc === reqLabel);
                  const done = matches.length > 0;
                  return (
                    <div key={i} className={`scx-pub-checklist-item${done ? ' done' : ''}`} data-testid={`secure-checklist-item-${i}`}>
                      {done ? <CheckCircle2 size={13} /> : <FileText size={12} />}
                      <span style={{ flex: 1 }}>{reqLabel}{done && ` — ${matches.length} file${matches.length > 1 ? 's' : ''} uploaded`}</span>
                      <button
                        type="button" className="scx-pub-checklist-upload-btn"
                        disabled={busy}
                        onClick={() => { setUploadTarget(reqLabel); itemFileInputRef.current.click(); }}
                        data-testid={`secure-checklist-upload-${i}`}
                      >
                        {done ? '+ Add another' : 'Upload'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="scx-pub-dz-label">{link.requested_documents?.length > 0 ? 'Other documents' : 'Upload documents'}</div>
            <div
              className={`scx-pub-dropzone${dragOver ? ' dragover' : ''}`}
              onClick={() => { setUploadTarget(null); fileInputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); setUploadTarget(null); uploadFiles(e.dataTransfer.files, null); }}
              data-testid="secure-dropzone"
            >
              <UploadCloud size={26} style={{ opacity: 0.5 }} />
              <div className="scx-pub-dz-text">{busy ? 'Uploading…' : 'Click to browse or drag files here'}</div>
              <input ref={fileInputRef} type="file" multiple onChange={(e) => uploadFiles(e.target.files, null)} data-testid="secure-file-input" />
            </div>
            {err && <div className="scx-pub-error" data-testid="secure-upload-error">{err}</div>}
            {docs.length > 0 && (
              <div data-testid="secure-uploaded-list">
                {docs.map((d, i) => (
                  <div key={i} className="scx-pub-file-row" data-testid={`secure-uploaded-item-${i}`}>
                    <CheckCircle2 size={16} className="scx-pub-success-ic" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="scx-pub-file-name">{d.filename}</div>
                      <div className="scx-pub-file-sub">{d.requested_doc ? `${d.requested_doc} · ` : ''}Uploaded {new Date(d.uploaded_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="scx-pub-foot">Secured by LegacyCRM SecureCX</div>
      </div>
    </div>
  );
};

export default PublicSecureCX;
